/**
 * The resource bar. Persistent across every screen, exactly as in the mockups.
 *
 * Everything here is live HTML text over a generated steel plate -- no numbers are ever
 * baked into art. The Heat cell is the loudest element on the screen by design, because
 * Heat is the load-bearing mechanic.
 *
 * TWO BARS, since Phase 18. The plate below is the desktop and tablet bar, and its cells now
 * SHRINK before any of them is dropped: they were all `flex: none` except Heat, so Heat, the one
 * cell the design calls load-bearing, was the first thing off the screen. It read "QUI" with no
 * meter at 1366 and was gone entirely at 1180. Now the Era clock and the Legend score are the
 * cells that give way, at the widths where they must, and Heat never does.
 *
 * Under 768px `PhoneBar` renders instead: one row of the four numbers the burst session reads
 * without tapping (GAME-DESIGN 1.2), with everything else a tap away on the portrait.
 */

import { useState } from 'react'
import { JEST } from '../build'
import { A } from '../art'
import { Icon } from '../ui/Icon'
import { Cells, Face, Meter, Row } from '../ui/Kit'
import { sz, useViewport } from '../ui/viewport'
import { useSession } from '../game/session'
import { LINE_NAME, LINE_SHORT } from '@ashport/shared'
import {
  clock,
  exact,
  heatBand,
  heatLabel,
  heatNote,
  money,
  nextTick,
  short,
  useGame,
} from '../game/state'

function Divider() {
  return (
    <div
      style={{
        width: 1,
        alignSelf: 'stretch',
        margin: '10px 0',
        background: 'linear-gradient(180deg, transparent, var(--line), transparent)',
        flex: 'none',
      }}
    />
  )
}

function Cell({
  children,
  grow,
  pad = 14,
  shrink,
}: {
  children: React.ReactNode
  grow?: number
  pad?: number
  /** Let the cell give up padding-room before the bar runs out. Default is rigid, as before. */
  shrink?: boolean
}) {
  return (
    <div
      style={{
        padding: `0 ${pad}px`,
        flex: grow ? `${grow} 1 0` : shrink ? '0 1 auto' : 'none',
        minWidth: 0,
        // A cell that shrinks clips at its own edge rather than painting over its neighbour.
        overflow: shrink ? 'hidden' : undefined,
      }}
    >
      {children}
    </div>
  )
}

export function TopBar() {
  const { vp, width, stacked } = useViewport()
  // The one-row bar wherever the rails stack: under 1024px the plate's seven cells do not fit
  // beside a rail, and a portrait tablet is a wide phone as far as the bar is concerned.
  if (stacked) return <PhoneBar />
  return <WideBar tablet={vp === 'tablet'} width={width} />
}

function WideBar({ tablet, width }: { tablet: boolean; width: number }) {
  const { player: p, era, now } = useGame()
  // Priced by the server against this player's Heat and tier. Zero until boot lands.
  const launderRateBp = useSession((s) => s.me?.launderRateBp ?? 0)
  const band = heatBand(p.heat)
  const eSec = nextTick(p.energyAt, now, 240_000)
  const nSec = nextTick(p.nerveAt, now, 720_000)
  // What gives way, and where. Legend is on the Ledger; the Era clock is on the Ledger too.
  const showLegend = width >= 1440
  const showEra = width >= 1180
  const pad = tablet ? 10 : 14

  return (
    <header
      style={{
        height: 'var(--hdr)',
        display: 'flex',
        alignItems: 'stretch',
        flex: 'none',
        position: 'relative',
        // The bar is a fixed-height plate. Anything that does not fit is cut at its edge rather
        // than allowed to paint across the screen below it.
        overflow: 'hidden',
        background: `linear-gradient(180deg, rgba(28,37,44,.82), rgba(8,11,13,.95)), url(${A('ui.materials')}) center/cover`,
        borderBottom: '1px solid var(--line)',
        boxShadow: '0 8px 26px -12px #000',
      }}
    >
      {/* brass edge light along the bottom of the plate */}
      <div
        style={{
          position: 'absolute',
          inset: 'auto 0 0',
          height: 1,
          background: 'linear-gradient(90deg, transparent, var(--line-brass), transparent)',
        }}
      />

      {/* --- identity ---------------------------------------------------- */}
      <Cell pad={12} shrink>
        <Row gap={11} style={{ height: '100%' }}>
          {/*
            * THE PLAYER'S OWN FACE AND THE PLAYER'S OWN NAME.
            *
            * This was `A('char.sallie')` captioned `{p.handle} Marchetti`, so every account in the
            * game wore Sallie Marchetti's portrait and was greeted with a surname they had not
            * chosen, in a family most of them are not in. `me.portraitId` and `me.displayName` were
            * both already on the wire and both already read by `state.ts`.
            */}
          <Face
            src={A(p.portraitId as Parameters<typeof A>[0], 'ui.avatar.socket_square')}
            alt={p.name}
            size={62}
            ring
            style={{ ['--clip' as string]: '6px' }}
          />
          <div style={{ minWidth: tablet ? 120 : 168 }}>
            <div
              className="t-head"
              style={{
                fontSize: 15,
                color: 'var(--gold)',
                letterSpacing: '.06em',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {p.name === '' ? p.handle : p.name}
            </div>
            <Row gap={7} style={{ marginTop: 2 }}>
              <span className="t-label" style={{ color: 'var(--ink)' }}>
                Level {p.level}
              </span>
              <span className="t-mono" style={{ color: 'var(--ink-dim)', fontSize: sz(10), whiteSpace: 'nowrap' }}>
                {p.xp.toLocaleString()} / {p.xpNext.toLocaleString()} XP
              </span>
            </Row>
            <div style={{ marginTop: 4 }}>
              <Meter v={p.xp} max={p.xpNext} a="var(--brass-deep)" b="var(--gold)" h={3} />
            </div>
            <Row gap={5} style={{ marginTop: 5 }}>
              <Icon n="rank" s={11} c="var(--brass-lit)" />
              <span className="t-label" style={{ fontSize: sz(9.5) }}>Rank</span>
              <span
                className="t-head"
                style={{ fontSize: 11, color: 'var(--ink-hi)', letterSpacing: '.1em' }}
                title={p.line !== '' ? `${p.rank}, ${LINE_NAME[p.line]}` : undefined}
              >
                {p.rank}
              </span>
              {/* PHASE 24. The Line, where the cell has room for one more word; the title carries it everywhere. */}
              {p.line !== '' && width >= 1600 && (
                <span className="t-head" style={{ fontSize: 11, color: 'var(--brass-lit)', letterSpacing: '.1em' }}>
                  {LINE_SHORT[p.line]}
                </span>
              )}
            </Row>
          </div>
        </Row>
      </Cell>

      <Divider />

      {/* --- energy ------------------------------------------------------ */}
      <Cell pad={pad} shrink>
        <Row gap={6} style={{ marginTop: 16 }}>
          <Icon n="energy" s={13} c="var(--energy)" fill />
          <span className="t-label" style={{ color: 'var(--energy)' }}>Energy</span>
        </Row>
        <div className="t-num" style={{ fontSize: 25, color: 'var(--ink-hi)', marginTop: 1, whiteSpace: 'nowrap' }}>
          {p.energy}
          <span style={{ color: 'var(--ink-dim)', fontSize: 16 }}> / {p.energyMax}</span>
        </div>
        {/*
          A FULL BAR SHOWS NO COUNTDOWN, AND THE REASON IS THAT THE OLD ONE WAS FROZEN RATHER
          THAN COUNTING. `settle` parks `at` at `now` the moment a bar reaches its cap
          (`shared/formulas.ts:105`, GAME-DESIGN 2.2 rule 2), so `asOfMs` equals server-now on
          every tick and `nextTick` returns a whole period forever: the line read "+1 in 4:00"
          and never moved. The server was right at every layer; only this cell was wrong.

          It is worth a conditional rather than a shrug because it taught the exact opposite of
          what `job_first` says out loud: "the only way to lose any is to let the bar sit full
          while you are asleep." A countdown under a full bar says a full bar is still earning.
        */}
        <div className="t-mono" style={{ fontSize: sz(10), color: 'var(--ink-dim)', whiteSpace: 'nowrap' }}>
          {p.energy >= p.energyMax ? 'full' : `+1 in ${short(eSec)}`}
        </div>
      </Cell>

      <Divider />

      {/* --- nerve ------------------------------------------------------- */}
      <Cell pad={pad} shrink>
        <Row gap={6} style={{ marginTop: 16 }}>
          <Icon n="nerve" s={13} c="var(--nerve)" />
          <span className="t-label" style={{ color: 'var(--nerve)' }}>Nerve</span>
        </Row>
        <div className="t-num" style={{ fontSize: 25, color: 'var(--ink-hi)', marginTop: 1, whiteSpace: 'nowrap' }}>
          {p.nerve}
          <span style={{ color: 'var(--ink-dim)', fontSize: 16 }}> / {p.nerveMax}</span>
        </div>
        <div className="t-mono" style={{ fontSize: sz(10), color: 'var(--ink-dim)', whiteSpace: 'nowrap' }}>
          {p.nerve >= p.nerveMax ? 'full' : `+1 in ${short(nSec)}`}
        </div>
      </Cell>

      <Divider />

      {/* --- money ------------------------------------------------------- */}
      <Cell pad={pad} shrink>
        <Row gap={6} style={{ marginTop: 16 }}>
          <Icon n="dirty" s={13} c="var(--dirty)" />
          <span className="t-label" style={{ color: 'var(--dirty)' }}>Dirty Cash</span>
        </Row>
        <div className="t-num" title={exact(p.dirty)} style={{ fontSize: 25, color: 'var(--dirty)', marginTop: 1, whiteSpace: 'nowrap' }}>
          {money(p.dirty)}
        </div>
        <div
          className="t-mono"
          style={{
            fontSize: sz(10),
            color: 'var(--dirty)',
            opacity: 0.72,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {p.heat >= 25 ? 'fences are paying under book' : 'fences are paying book'}
        </div>
      </Cell>

      <Divider />

      <Cell pad={pad} shrink>
        <Row gap={6} style={{ marginTop: 16 }}>
          <Icon n="clean" s={13} c="var(--clean)" />
          <span className="t-label" style={{ color: 'var(--clean)' }}>Clean Cash</span>
        </Row>
        <div className="t-num" title={exact(p.clean)} style={{ fontSize: 25, color: 'var(--clean)', marginTop: 1, whiteSpace: 'nowrap' }}>
          {money(p.clean)}
        </div>
        {/* The rate the server priced for THIS player, already penalised by Heat. */}
        <div className="t-mono" style={{ fontSize: sz(10), color: 'var(--ink-dim)', whiteSpace: 'nowrap' }}>
          laundering at {(launderRateBp / 100).toFixed(1)}%
        </div>
      </Cell>

      <Divider />

      {/*
        --- heat, the loudest cell on the bar ----------------------------

        THIS CELL USED TO PAINT ON TOP OF THE ERA CLOCK, on every screen, at every width narrow
        enough to matter, including 1440.

        It is the only cell with `grow`, so it is `flex: 1 1 0` while every other cell is
        `flex: none`, which is `0 0 auto` and therefore cannot shrink. When the bar runs out of
        room, this is the only cell that gives, and a flex item cannot shrink below the min-content
        width of what is inside it. The meter reserved 90px, the percentage is 24px type and the
        band note is a whole sentence, so min-content stayed far wider than the space left and the
        overflow simply painted over the neighbour: "0%" landed across "Era 1", and "Nobody is
        looking at you." wrapped to three lines across "The Iron Pact".

        Nothing was clipped, nothing errored, and it was legible enough to read past. `overflow`
        is the fix and the ellipsis is the honest half of it: the note is the least load-bearing
        thing on the bar, so it is the thing that gets shortened.

        Phase 18 added the floor: `1 1 0` let the cell shrink to nothing once the rigid cells had
        taken the bar, which is how it vanished at 1180. It keeps at least 170px now, and the
        cells to its right are the ones that go: Legend under 1440, the Era clock under 1180.
      */}
      <div style={{ padding: `0 ${pad + 2}px`, flex: '1 1 210px', minWidth: 210 }}>
        <div style={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
          {band === 'critical' || band === 'rico' ? <div className="sweep" /> : null}
          <Row gap={8} style={{ marginTop: 14 }}>
            <Icon n="heat" s={19} c="var(--heat)" fill />
            <span className="t-head" style={{ fontSize: 15, color: 'var(--heat)', letterSpacing: '.16em' }}>
              Heat
            </span>
            {/* 40, not 90. The meter is decoration for a number that is printed beside it. */}
            <div style={{ flex: '1 1 0', minWidth: 40 }}>
              <Cells v={p.heat} crit={band === 'critical' || band === 'rico'} />
            </div>
            <span className="t-num" style={{ fontSize: 24, color: 'var(--heat)', flex: 'none' }}>
              {p.heat}%
            </span>
          </Row>
          <Row gap={7} style={{ marginTop: 5 }}>
            <span
              className="t-head"
              style={{ fontSize: 11, color: 'var(--arrest)', letterSpacing: '.16em', flex: 'none' }}
            >
              {heatLabel[band]}
            </span>
            {/* The band name above is the signal; this sentence is the gloss, so this is what
                gives way first. `minWidth: 0` is required: a flex item defaults to
                `min-width: auto` and will not shrink below its longest word without it. */}
            <span
              title={heatNote[band]}
              style={{
                fontSize: sz(10.5),
                color: 'var(--ink-dim)',
                flex: '1 1 0',
                minWidth: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {heatNote[band]}
            </span>
            {(band === 'critical' || band === 'rico') && (
              <Icon n="warn" s={13} c="var(--arrest)" className="pulse" />
            )}
          </Row>
        </div>
      </div>

      {/* --- the till, when there is something in it. Phase 21. ----------- */}
      <TillCell />

      {/* --- era clock --------------------------------------------------- */}
      {showEra && (
        <>
          <Divider />
          <Cell pad={pad} shrink>
            <div
              className="t-head"
              style={{ fontSize: 12.5, color: 'var(--ink-hi)', marginTop: 17, letterSpacing: '.11em', whiteSpace: 'nowrap' }}
            >
              Era {era.n} &middot; Week {era.week} of {era.of}
            </div>
            <div style={{ fontSize: 11, color: 'var(--gold)', marginTop: 1, whiteSpace: 'nowrap' }}>{era.name}</div>
            <Row gap={5} style={{ marginTop: 4 }}>
              <Icon n="clock" s={11} c="var(--ink-dim)" />
              <span className="t-mono" style={{ fontSize: sz(10.5), color: 'var(--ink-mid)', whiteSpace: 'nowrap' }}>
                {clock((era.endsAt - now) / 1000)}
              </span>
            </Row>
          </Cell>
        </>
      )}

      {/* --- legend ------------------------------------------------------ */}
      {showLegend && (
        <>
          <Divider />
          <Cell pad={16}>
            <Row gap={9} style={{ height: '100%' }}>
              <Icon n="legend" s={26} c="var(--gold)" />
              <div>
                <div className="t-label" style={{ color: 'var(--gold)' }}>Legend Score</div>
                <div className="t-num" style={{ fontSize: 24, color: 'var(--ink-hi)', lineHeight: 1.05 }}>
                  {p.legend.toLocaleString()}
                </div>
                {/*
                  "Top 1.2% of all time" was hardcoded, and the Legend score beside it is real. A level
                  two character with a Legend score of ZERO was being told they were in the top 1.2
                  percent, which is the Jobs rewards panel lesson in a smaller font: a displayed number
                  with no system behind it teaches players the numbers are decorative.

                  The percentile needs the standings, which is `legend_board` in the permanent database
                  and a KV snapshot on a 60 second cron. Phase 12 owns it. Until then the line is gone
                  rather than invented.
                */}
              </div>
            </Row>
          </Cell>
        </>
      )}

      {/*
        * SIGN OUT, which no screen offered.
        *
        * `signOut` has been on the store since Phase 1, `POST /api/auth/logout` has been on the
        * server just as long, and nothing anywhere called either. A session ended when the
        * `__Host-` cookie expired and in no other way, so a player on a shared machine could not
        * leave their account, which is a security answer as much as a convenience one.
        *
        * It sits at the far right of the top bar, the last thing in the strip, because it is the
        * one control on this screen a player should never hit by accident.
        */}
      <Divider />
      <Cell pad={4}>
        <NotifyBell />
      </Cell>
      {/* PHASE 26. Under the Jest build the platform owns the session, so there is nothing to sign out of (1ag.8). */}
      {!JEST && (
        <Cell pad={8}>
          <SignOut />
        </Cell>
      )}
    </header>
  )
}

/**
 * THE PHONE BAR: the four numbers, one row, and the rest behind the portrait.
 *
 * GAME-DESIGN 1.2, minute zero: "Read three numbers without tapping: Energy, Heat band, and which
 * district the Beat is on." Energy, Nerve, Dirty and Heat are here; the Beat is on the ticker
 * below the screen. Clean cash, the rank, the XP bar, the Era clock and the Legend score are one
 * tap away on the portrait, which is the only control in the row that is not a number.
 */
function PhoneBar() {
  const { player: p, era, now } = useGame()
  const { vp } = useViewport()
  const launderRateBp = useSession((s) => s.me?.launderRateBp ?? 0)
  const band = heatBand(p.heat)
  const [open, setOpen] = useState(false)

  const num = (v: React.ReactNode, c: string) => (
    <div className="t-num" style={{ fontSize: 17, color: c, lineHeight: 1.05, whiteSpace: 'nowrap' }}>
      {v}
    </div>
  )
  const label = (t: string, c: string) => (
    <div className="t-label" style={{ fontSize: sz(8.5), color: c, letterSpacing: '.14em' }}>
      {t}
    </div>
  )

  return (
    <>
      <header
        style={{
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '6px 10px',
          minHeight: 56,
          background: `linear-gradient(180deg, rgba(28,37,44,.82), rgba(8,11,13,.95)), url(${A('ui.materials')}) center/cover`,
          borderBottom: '1px solid var(--line)',
          boxShadow: '0 8px 26px -12px #000',
        }}
      >
        <button
          className="icon-btn"
          onClick={() => setOpen(true)}
          aria-label={`${p.name === '' ? p.handle : p.name}, level ${p.level}, ${p.rank}. Open details`}
          aria-haspopup="dialog"
          aria-expanded={open}
          style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', flex: 'none' }}
        >
          <Face
            src={A(p.portraitId as Parameters<typeof A>[0], 'ui.avatar.socket_square')}
            alt=""
            size={40}
            ring
            style={{ ['--clip' as string]: '4px' }}
          />
        </button>

        <div style={{ flex: '1 1 0', minWidth: 0, display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 6 }}>
          <div style={{ minWidth: 0 }}>
            {label('Energy', 'var(--energy)')}
            {num(
              <>
                {p.energy}
                <span style={{ color: 'var(--ink-dim)', fontSize: 12 }}>/{p.energyMax}</span>
              </>,
              'var(--ink-hi)',
            )}
          </div>
          <div style={{ minWidth: 0 }}>
            {label('Nerve', 'var(--nerve)')}
            {num(
              <>
                {p.nerve}
                <span style={{ color: 'var(--ink-dim)', fontSize: 12 }}>/{p.nerveMax}</span>
              </>,
              'var(--ink-hi)',
            )}
          </div>
          <div style={{ minWidth: 0 }} title={exact(p.dirty)}>
            {label('Dirty', 'var(--dirty)')}
            {num(money(p.dirty), 'var(--dirty)')}
          </div>
        </div>

        <div style={{ flex: '1 1 0', minWidth: 96, maxWidth: 150 }}>
          <Row gap={6}>
            <Icon n="heat" s={13} c="var(--heat)" fill />
            <span className="t-label" style={{ fontSize: sz(8.5), color: 'var(--heat)', letterSpacing: '.14em' }}>
              Heat
            </span>
            <span className="t-num" style={{ fontSize: 15, color: 'var(--heat)', marginLeft: 'auto' }}>
              {p.heat}%
            </span>
          </Row>
          <div style={{ marginTop: 3 }}>
            <Cells v={p.heat} n={10} crit={band === 'critical' || band === 'rico'} />
          </div>
          <div
            className="t-head"
            style={{ fontSize: sz(9), color: 'var(--arrest)', letterSpacing: '.14em', marginTop: 2, whiteSpace: 'nowrap' }}
          >
            {heatLabel[band]}
          </div>
        </div>
        {/*
          * THE BELL, WHEREVER THERE IS NO MORE SHEET. Found in Phase 26's visual pass: the card
          * opened from More on a phone and from the bell on the wide bar, and a portrait tablet has
          * neither, so from Phase 23 until now nothing on a tablet could open Notifications at all.
          * The phone keeps it behind More, where it was.
          */}
        {vp !== 'phone' && (
          <div style={{ flex: 'none', height: 44, display: 'grid' }}>
            <NotifyBell />
          </div>
        )}
      </header>

      {open && (
        <div className="sheet" onClick={() => setOpen(false)} role="presentation">
          <div role="dialog" aria-label="Your standing" onClick={(e) => e.stopPropagation()} style={{ padding: '16px 18px 18px' }}>
            <Row gap={12}>
              <Face
                src={A(p.portraitId as Parameters<typeof A>[0], 'ui.avatar.socket_square')}
                alt=""
                size={56}
                ring
                style={{ ['--clip' as string]: '5px' }}
              />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="t-head" style={{ fontSize: 16, color: 'var(--gold)', letterSpacing: '.06em' }}>
                  {p.name === '' ? p.handle : p.name}
                </div>
                <Row gap={7} style={{ marginTop: 3 }}>
                  <Icon n="rank" s={11} c="var(--brass-lit)" />
                  <span className="t-head" style={{ fontSize: 12, color: 'var(--ink-hi)', letterSpacing: '.1em' }}>
                    {p.rank}
                  </span>
                  <span className="t-label" style={{ color: 'var(--ink)' }}>
                    Level {p.level}
                  </span>
                  {p.line !== '' && (
                    <span className="t-mono" style={{ fontSize: sz(10), color: 'var(--brass-lit)' }}>
                      {LINE_NAME[p.line]}
                    </span>
                  )}
                </Row>
                <div style={{ marginTop: 6 }}>
                  <Meter v={p.xp} max={p.xpNext} a="var(--brass-deep)" b="var(--gold)" h={4} />
                </div>
                <div className="t-mono" style={{ color: 'var(--ink-dim)', marginTop: 3 }}>
                  {p.xp.toLocaleString()} / {p.xpNext.toLocaleString()} XP
                </div>
              </div>
            </Row>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10, marginTop: 16 }}>
              <div>
                <div className="t-label" style={{ color: 'var(--clean)' }}>Clean Cash</div>
                <div className="t-num" title={exact(p.clean)} style={{ fontSize: 22, color: 'var(--clean)' }}>
                  {money(p.clean)}
                </div>
                <div className="t-mono" style={{ color: 'var(--ink-dim)' }}>
                  laundering at {(launderRateBp / 100).toFixed(1)}%
                </div>
              </div>
              <div>
                <div className="t-label" style={{ color: 'var(--gold)' }}>Legend Score</div>
                <div className="t-num" style={{ fontSize: 22, color: 'var(--ink-hi)' }}>
                  {p.legend.toLocaleString()}
                </div>
              </div>
              <div>
                <div className="t-label" style={{ color: 'var(--dirty)' }}>Dirty Cash</div>
                <div className="t-num" title={exact(p.dirty)} style={{ fontSize: 22, color: 'var(--dirty)' }}>
                  {money(p.dirty)}
                </div>
                <div className="t-mono" style={{ color: 'var(--ink-dim)' }}>
                  {p.heat >= 25 ? 'fences are paying under book' : 'fences are paying book'}
                </div>
              </div>
              <div>
                <div className="t-label" style={{ color: 'var(--ink-hi)' }}>
                  Era {era.n} &middot; Week {era.week} of {era.of}
                </div>
                <div style={{ fontSize: 13, color: 'var(--gold)', marginTop: 2 }}>{era.name}</div>
                <div className="t-mono" style={{ color: 'var(--ink-mid)' }}>{clock((era.endsAt - now) / 1000)}</div>
              </div>
            </div>

            <div
              style={{
                marginTop: 14,
                paddingTop: 10,
                borderTop: '1px solid var(--line-soft)',
                fontSize: 12,
                color: 'var(--ink-mid)',
                lineHeight: 1.5,
              }}
            >
              {heatNote[band]}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

/**
 * WHAT THE FRONTS HAVE TAKEN SINCE THE LAST COLLECT, on the bar, whenever it is above zero.
 *
 * The number a player with a front logs in for, and until Phase 21 it lived on one Home card
 * that rendered only once a front was owned. It reads the gear slice, which Home and the Market
 * both load; a session that has opened neither shows nothing rather than a stale zero.
 */
function TillCell() {
  const gear = useSession((s) => s.gear)
  if (!gear || gear.fronts === 0 || gear.till <= 0) return null
  return (
    <>
      <Divider />
      <Cell pad={12} shrink>
        <Row gap={6} style={{ marginTop: 16 }}>
          <Icon n="clean" s={13} c="var(--clean)" />
          <span className="t-label" style={{ color: 'var(--clean)' }}>The Till</span>
        </Row>
        <div className="t-num" title={exact(gear.till)} style={{ fontSize: 25, color: gear.tillCapped ? 'var(--heat)' : 'var(--clean)', marginTop: 1, whiteSpace: 'nowrap' }}>
          {money(gear.till)}
        </div>
        {/* One short line: the front count is on the Fronts tab, and the cell clipped it at 1440. */}
        <div className="t-mono" style={{ fontSize: sz(10), color: gear.tillCapped ? 'var(--heat)' : 'var(--ink-dim)', whiteSpace: 'nowrap' }}>
          {gear.tillCapped ? 'full, not earning' : `${gear.tillHours}h in`}
        </div>
      </Cell>
    </>
  )
}

/** PHASE 23. The card, from the wide bar, next to the one other account-level control. */
export function NotifyBell() {
  const openNotify = useSession((s) => s.openNotify)
  return (
    <button
      className="icon-btn"
      onClick={openNotify}
      aria-label="Notifications"
      title="Notifications"
      style={{
        height: '100%',
        display: 'grid',
        placeItems: 'center',
        background: 'none',
        border: 0,
        cursor: 'pointer',
        padding: '0 4px',
      }}
    >
      <Icon n="bell" s={15} c="var(--ink-dim)" />
    </button>
  )
}

export function SignOut({ labelled = false }: { labelled?: boolean }) {
  const signOut = useSession((s) => s.signOut)
  const busy = useSession((s) => s.busy)
  if (labelled) {
    return (
      <button
        className="btn"
        data-v="ghost"
        data-full
        onClick={() => void signOut()}
        disabled={busy}
        style={{ justifyContent: 'flex-start' }}
      >
        <Icon n="close" s={13} c="var(--ink-dim)" />
        Sign out
      </button>
    )
  }
  return (
    <button
      className="icon-btn"
      onClick={() => void signOut()}
      disabled={busy}
      aria-label="Sign out"
      title="Sign out"
      style={{
        height: '100%',
        display: 'grid',
        placeItems: 'center',
        background: 'none',
        border: 0,
        cursor: busy ? 'default' : 'pointer',
        padding: '0 4px',
        opacity: busy ? 0.4 : 0.75,
      }}
    >
      <Icon n="close" s={13} c="var(--ink-dim)" />
    </button>
  )
}
